import axios from 'axios'

import { API_BASE_URL } from './baseURL'
import { createApiClient } from './client'
import type { ApiEnvelope, AuthResponse } from './types'

function cookieValue(baseName: string): string | null {
  // Production uses a domain-scoped __Secure cookie. Prefer hardened names so
  // a stale legacy cookie left by an earlier deployment cannot poison CSRF
  // validation after a page reload.
  const candidates = [`__Secure-${baseName}`, `__Host-${baseName}`, baseName]
  const cookies = Object.fromEntries(
    document.cookie.split(';').map((item) => item.trim().split('='))
      .filter((parts) => parts.length >= 2)
      .map(([key, ...value]) => [key, decodeURIComponent(value.join('='))]),
  )
  return candidates.map((key) => cookies[key]).find(Boolean) ?? null
}

const refreshTransport = axios.create({ baseURL: API_BASE_URL, withCredentials: true, timeout: 20_000 })

export async function refreshSession(): Promise<AuthResponse> {
  const csrfToken = cookieValue('h5CsrfToken')
  const headers: Record<string, string> = {}
  if (csrfToken) headers['X-CSRF-TOKEN'] = csrfToken
  const response = await refreshTransport.post<ApiEnvelope<AuthResponse>>('/h5/auth/refresh', {}, { headers })
  return unwrap(response.data)
}

export const apiRuntime = createApiClient({
  baseURL: API_BASE_URL,
  async refresh() { return (await refreshSession()).accessToken },
  onAuthFailure() { window.dispatchEvent(new CustomEvent('tenant-auth-expired')) },
})

export const apiClient = apiRuntime.client

type Json = string | number | boolean | null | Json[] | { [key: string]: Json }

/**
 * 后端 DumpSchema（exclude_none=false）把未填字段输出为显式 null，而前端
 * 类型一律声明为可选（undefined）。这里在 API 边界把 null 深度归一为
 * undefined，让「可选」的类型承诺对整棵响应树成立——消费端只防 undefined
 * 的逻辑（如坐标 toFixed）不再被 wire null 击穿。0/false/'' 等假值保留。
 */
function normalizeNulls<T>(value: T): T {
  if (Array.isArray(value)) return value.map((entry) => normalizeNulls(entry)) as T
  if (value !== null && typeof value === 'object') {
    const output: Record<string, Json | undefined> = {}
    for (const [key, entry] of Object.entries(value as Record<string, Json>)) {
      output[key] = entry === null ? undefined : (normalizeNulls(entry) as Json)
    }
    return output as T
  }
  return value
}

export function unwrap<T>(payload: ApiEnvelope<T> | T): T {
  const data = typeof payload === 'object' && payload !== null && 'data' in payload
    ? (payload as ApiEnvelope<T>).data
    : payload as T
  return normalizeNulls(data)
}
